import { useCallback, useEffect, useRef, useState } from "react";
import {
  MIN_IRR_DEPOSIT,
  getIrrCtoc,
  pollIrrCtoc,
  startIrrCtoc,
  uploadIrrCtocReceipt,
  type IrrCtocSession,
} from "@/api/irrCtoc";
import { errorMessage } from "@/api/auth";
import { useToast } from "@/toast/ToastContext";

const WINDOW_SEC = 300;
const POLL_MS = 15000;

function formatCard(card: string | null | undefined) {
  const d = (card || "").replace(/\D/g, "");
  if (d.length >= 16) return d.match(/.{1,4}/g)?.join("-") ?? d;
  return card || "—";
}

function nf(v?: number | null) {
  return (v || 0).toLocaleString("en-US");
}

function mmss(total: number) {
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

type Props = {
  token: string;
  onClose: () => void;
  onCredited: (balanceIrr: number) => void;
};

export function IrrCtocDeposit({ token, onClose, onCredited }: Props) {
  const toast = useToast();
  const [amountToman, setAmountToman] = useState("");
  const [session, setSession] = useState<IrrCtocSession | null>(null);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState("");
  const [secondsLeft, setSecondsLeft] = useState(WINDOW_SEC);
  const [expired, setExpired] = useState(false);
  const [receiptLocked, setReceiptLocked] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const paidRef = useRef(false);

  const markPaid = useCallback(
    (bal?: number | null) => {
      if (paidRef.current) return;
      paidRef.current = true;
      setStatus("شارژ شد");
      toast.ok("شارژ ریال");
      onCredited(typeof bal === "number" ? bal : 0);
    },
    [onCredited, toast]
  );

  function isCredited(r: { paid?: boolean; credited?: boolean; status?: string; message?: string }) {
    return (
      r.credited === true ||
      r.status === "credited" ||
      r.message === "already_credited" ||
      (r.paid === true && r.status === "credited")
    );
  }

  async function onStart() {
    const toman = Number(String(amountToman).replace(/,/g, "").replace(/[۰-۹]/g, (d) =>
      String("۰۱۲۳۴۵۶۷۸۹".indexOf(d))
    ));
    if (!Number.isFinite(toman) || toman <= 0) {
      toast.err("مبلغ");
      return;
    }
    const amountIrr = Math.round(toman) * 10;
    if (amountIrr < MIN_IRR_DEPOSIT) {
      toast.err(`حداقل ${nf(MIN_IRR_DEPOSIT / 10)} تومان`);
      return;
    }
    setBusy(true);
    setStatus("");
    try {
      const out = await startIrrCtoc(token, amountIrr);
      setSession(out);
      setSecondsLeft(WINDOW_SEC);
      setExpired(false);
      paidRef.current = false;
      if (isCredited(out)) markPaid(out.balanceIrr);
    } catch (e) {
      toast.err(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    if (!session?.intentId || session.paid || paidRef.current) return;
    const started = Date.now();
    const tick = setInterval(() => {
      const left = Math.max(0, WINDOW_SEC - Math.floor((Date.now() - started) / 1000));
      setSecondsLeft(left);
      if (left <= 0) {
        setExpired(true);
        clearInterval(tick);
      }
    }, 1000);
    const poll = setInterval(async () => {
      if (paidRef.current) return;
      try {
        const r = await pollIrrCtoc(token, session.intentId);
        if (isCredited(r)) {
          clearInterval(poll);
          clearInterval(tick);
          markPaid(r.balanceIrr);
        } else if (r.giftPaid) {
          setStatus("در انتظار شارژ");
        }
      } catch {
        /* keep polling */
      }
    }, POLL_MS);
    return () => {
      clearInterval(tick);
      clearInterval(poll);
    };
  }, [session?.intentId, session?.paid, token, markPaid]);

  async function onCopyCard() {
    const raw = (session?.cardNumber || "").replace(/\D/g, "");
    if (!raw) return;
    try {
      await navigator.clipboard.writeText(raw);
      toast.ok("کپی");
    } catch {
      toast.err("کپی");
    }
  }

  async function onRefresh() {
    if (!session?.intentId) return;
    setBusy(true);
    try {
      const out = await getIrrCtoc(token, session.intentId);
      setSession(out);
      if (isCredited(out)) markPaid(out.balanceIrr);
    } catch (e) {
      toast.err(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  async function onReceipt(file: File) {
    if (!session?.intentId) return;
    setBusy(true);
    setReceiptLocked(true);
    setStatus("بررسی رسید…");
    try {
      const r = await uploadIrrCtocReceipt(token, session.intentId, file);
      if (isCredited(r) || (r.verdict === "paid" && r.status === "credited")) {
        markPaid(r.balanceIrr);
      } else if (r.verdict === "mismatch") {
        setReceiptLocked(false);
        setStatus("نامطابق");
        toast.err("نامطابق");
      } else if (r.verdict === "unreadable") {
        setReceiptLocked(false);
        setStatus("خوانا نیست");
      } else {
        setStatus("در انتظار");
      }
    } catch (e) {
      setReceiptLocked(false);
      setStatus("");
      toast.err(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  if (!session) {
    return (
      <div className="card irr-ctoc-card" dir="rtl">
        <div className="row-between">
          <b>واریز ریال</b>
          <button type="button" className="chip" onClick={onClose}>
            ✕
          </button>
        </div>
        <label>
          مبلغ (تومان)
          <input
            className="ltr"
            inputMode="numeric"
            value={amountToman}
            onChange={(e) => setAmountToman(e.target.value.replace(/[^\d۰-۹,]/g, ""))}
            placeholder={`${nf(MIN_IRR_DEPOSIT / 10)}+`}
          />
        </label>
        <button type="button" className="primary" disabled={busy} onClick={() => void onStart()}>
          {busy ? "…" : "ادامه"}
        </button>
      </div>
    );
  }

  return (
    <div className="card irr-ctoc-card" dir="rtl">
      <div className="row-between">
        <b>کارت به کارت</b>
        <button type="button" className="chip" onClick={onClose} disabled={busy && !receiptLocked}>
          ✕
        </button>
      </div>

      <div className="irr-ctoc-amount ltr">{nf(session.amountIrr)} IRR</div>
      {session.holderDisplay && <p className="muted">{session.holderDisplay}</p>}

      <div className="irr-ctoc-card-row">
        <code className="irr-ctoc-card-num ltr">{formatCard(session.cardNumber)}</code>
        <button type="button" className="chip" onClick={() => void onCopyCard()}>
          کپی
        </button>
      </div>

      <div className="row-between">
        <span className={`irr-timer ${expired ? "err" : ""}`}>{mmss(secondsLeft)}</span>
        {status && <span className="chip-mini">{status}</span>}
      </div>

      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        capture="environment"
        hidden
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) void onReceipt(f);
          e.target.value = "";
        }}
      />

      <div className="row-actions">
        <button
          type="button"
          className="primary"
          disabled={busy || receiptLocked || expired || paidRef.current}
          onClick={() => fileRef.current?.click()}
        >
          رسید
        </button>
        <button type="button" className="chip" disabled={busy} onClick={() => void onRefresh()}>
          ↻
        </button>
      </div>
    </div>
  );
}
